import moment from "moment";
import DateUtils from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import { MAX_DATE } from "../components/AppToolbar";

interface Dates {
  startDate: Date;
  endDate: Date;
}

enum DateInMilliseconds {
  DAY = 86400000, // 24 * 60 * 60 * 1000
  WEEK = 604800000, // 7 * 24 * 60 * 60 * 1000
}

export enum Months {
  ALL = "All",
  JANUARY = "January",
  FEBRUARY = "February",
  MARCH = "March",
  APRIL = "April",
  MAY = "May",
  JUNE = "June",
  JULY = "July",
  AUGUST = "August",
  SEPTEMBER = "September",
  OCTOBER = "October",
  NOVEMBER = "November",
  DECEMBER = "December",
}

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default class OpsDateUtils {
  /**
   * Dictionary to get the name of the day of the week
   */
  public static dayOfWeekDictionary: { [key: number]: string } = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
  };

  /**
   * Check if a date is a weekend day (Saturday or Sunday)
   * @param date
   * @returns true if the date is a weekend, false if not
   */
  public static isWeekend(date: Date): boolean {
    return date.getDay() % 6 === 0;
  }

  public static ensureFormatDate = (date: Date | undefined): string => {
    if (typeof date === "undefined") {
      return "";
    }

    return DateUtils.formatAsDDMMMYYY(date);
  };

  /**
   * This method is used to get an array of the weeks of a month in a specific year.
   * @param year in number
   * @param month in number, starting by 0
   * @returns an array of the weeks of a month in the following format: [{startDate: 01/may/2022, endDate: 07/may/2022}]
   */
  public static getWeeksOfMonth(year: number, month: number): Dates[] {
    const moment = require("moment");
    const lastDayOfWeek = 6;

    const weeks: Dates[] = new Array<Dates>();

    const firstDay: Date = new Date(year, month, 1),
      lastDay: Date = new Date(year, month + 1, 0),
      daysInMonth: number = lastDay.getDate();

    let dayOfWeek: number = firstDay.getDay(),
      startDate: number = 0,
      endDate: number;

    for (let i = 1; i < daysInMonth + 1; i++) {
      if (i === 1) {
        startDate = i;
      }
      if (dayOfWeek === 0) {
        startDate = i - 1;
      }

      if (dayOfWeek === 5 || (i === daysInMonth && dayOfWeek !== 6)) {
        endDate = i;

        if (startDate !== endDate) {
          weeks.push({
            startDate: moment(new Date(year, month, startDate).toLocaleDateString("en")).format("DD/MMM/yyyy"),
            endDate: moment(new Date(year, month, endDate).toLocaleDateString("en")).format("DD/MMM/yyyy"),
          });
        }
      } else {
        //1 day week
        if (dayOfWeek === lastDayOfWeek && (i === 1 || i === daysInMonth)) {
          endDate = i;
          weeks.push({
            startDate: moment(new Date(year, month, endDate).toLocaleDateString("en")).format("DD/MMM/yyyy"),
            endDate: moment(new Date(year, month, endDate).toLocaleDateString("en")).format("DD/MMM/yyyy"),
          });
        }
      }
      dayOfWeek = new Date(year, month, i).getDay();
    }
    return weeks;
  }

  public static getWeekOfYear = (date: Date): number => {
    return parseInt(moment(date).format("W"));
  }

  /**
   * Gets the current day at time 0
   * @returns the current day
   */
  public static getCurrentDay = (): Date => {
    const newDate = new Date();
    return newDate;
  };

  public static getMaxDate = (): Date => {
    return MAX_DATE;
  }
  /**
   * Gets the first day of a given week
   * @param date Date object of the week - to get the year
   * @param week Number of the week from year
   * @returns Date object of the first day of the week
   */
  public static getFirstDayOfWeek = (date: Date, week: number): Date => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    return new Date(firstDayOfYear.getTime() - DateInMilliseconds.DAY * firstDayOfYear.getDay() + DateInMilliseconds.WEEK * week);
  };

  /**
   * Gets the first day of a given week in the same month
   * @param date Date object of the week - to get the year and month
   * @param week Number of the week from year
   * @returns Date object of the first day of the week. If the day is in the previous month, returns the first day of the month.
   */
  public static getFirstDayOfWeekInMonth = (date: Date, week: number): Date => {
    const firstDayOfWeek = this.getFirstDayOfWeek(date, week);

    // the first day of the week can be in the previous month
    // in that case, we want to use the first day of the month
    if (firstDayOfWeek.getMonth() !== date.getMonth()) {
      const firstDayOfMonth = date;
      firstDayOfMonth.setDate(1);
      return firstDayOfMonth;
    }

    return firstDayOfWeek;
  };

  public static isSameOrBefore = (originalDate: Date, dateToCompare: Date): boolean => {
    return moment(originalDate).isSameOrBefore(moment(dateToCompare));
  };

  public static isBefore = (originalDate: Date, dateToCompare: Date): boolean => {
    return moment(originalDate).isBefore(moment(dateToCompare));
  };

  public static isSameDay = (originalDate: Date, dateToCompare: Date): boolean => {
    return (
      new Date(originalDate).getDate() === new Date(dateToCompare).getDate() &&
      new Date(originalDate).getMonth() === new Date(dateToCompare).getMonth()
    );
  };

  public static isEqualToToday = (date: Date): boolean => {
    return moment(date).isSame(new Date(), "day");
  };

  public static formatInUTCAsDDMMMYYY(date: Date | string) {
    return moment.utc(date).format("DD/MMM/YYYY");
  }

  /**
   * Formats the date by using "dddd DD, MMMYYYY" format
   * @param date the date to be formatted
   */
  public static formatInUTCAsDDDMMMYYY(date: Date) {
    return moment.utc(date).format("ddd DD, MMM YYYY");
  }

  /**
   * Formats the date by using "dddd DD, MMM" format
   * @param date the date to be formatted
   */
  public static formatInUTCAsDDDMMM(date: Date) {
    return moment.utc(date).format("ddd DD, MMM ");
  }

    /**
   * Formats the date by using "DD" format
   * @param date the date to be formatted
   */
    public static formatInUTCOnlyDay(date: Date) {
      return moment.utc(date).format("DD");
    }

  public static isAfter = (originalDate: Date, dateToCompare: Date): boolean => {
    return moment(originalDate).isAfter(moment(dateToCompare));
  };

  public static isSameOrAfter = (originalDate: Date, dateToCompare: Date): boolean => {
    return moment(originalDate).isSameOrAfter(moment(dateToCompare));
  };

  public static isBetween = (startDate: Date, endDate: Date, dateToCompare: Date): boolean => {
    return moment(dateToCompare).isBetween(startDate, endDate);
  };

  public static isBetweenOrEqual = (startDate: Date, endDate: Date, dateToCompare: Date): boolean => {
    let date = new Date(endDate);
    date.setDate(date.getDate() + 1);
    return (
      new Date(dateToCompare).getTime() >= new Date(startDate).getTime() && new Date(dateToCompare).getTime() <= new Date(date).getTime()
    );
  };
  public static toUTC(date: Date): Date {
    let offset: number = date.getTimezoneOffset() * 60000;
    if (offset > 0) {
      OpsDateUtils.setHoursToZero(date);
      return new Date(date.getTime() + offset);
    } else {
      let newDate = new Date(date.getTime() - offset);
      return newDate;
    }
  }

  public static toUTCWithSetHours(date: Date): Date {
    let offset: number = date.getTimezoneOffset() * 60000;
    if (offset > 0) {
      OpsDateUtils.setHoursToZero(date);
      return new Date(date.getTime() + offset);
    } else {
      date.setHours(12, 0, 0, 0);
      let newDate = new Date(date.getTime() - offset);
      return newDate;
    }
  }

  public static toStartOfDay(date: Date): Date {
    return moment(date).clone().startOf("day").toDate();
  }

  public static utcOffset = (date: Date) => {
    let localDate = new Date(date);
    const offset = localDate.getTimezoneOffset();
    if (offset < 0) {
      return localDate;
    }
    const processedDate = new Date(localDate.getTime() + offset * 60 * 1000);
    return processedDate;
  };

  public static toLocalDate = (dateValue: Date) => {
    return new Date(dateValue);
  };

  public static equalDates = (oneDate: Date, otherDate: Date) => {
    return (
      oneDate.getDay() === otherDate.getDay() &&
      oneDate.getMonth() === otherDate.getMonth() &&
      oneDate.getFullYear() === otherDate.getFullYear()
    );
  };

  public static setHoursToZero = (date: Date) => {
    date.setHours(0, 0, 0, 0);
    return date;
  };

  /**
   * Formats the date by using "DD-MM" format
   * @param date the date to be formatted
   */
  public static formatUTCAsDDMMM(date: Date) {
    return moment.utc(date).format("DD/MMM");
  }

  /**
   * Formats the date by using DD/MMM/YYYY format
   * @param date the date to be formatted
   */
  public static formatUTCAsDDMMMYYY(date: Date) {
    return moment.utc(date).format("DD/MMM/YYYY");
  }

  public static ensureFormatDateUTC = (date: Date | undefined): string => {
    if (typeof date === "undefined") {
      return "";
    }

    return this.formatUTCAsDDMMMYYY(date);
  };

  public static getTimezoneOffset = (date: Date) => date.getTimezoneOffset() * 60000;

  public static makeLocalAppearUTC = (value: string | number | Date) => {
    if (value === null) {
      return null;
    }
    const dateTime = new Date(value);
    const utcFromLocal = new Date(dateTime.getTime() + this.getTimezoneOffset(dateTime));
    return utcFromLocal;
  };

  public static localToUTC = (dateTime: any) => {
    if (dateTime === null) {
      return null;
    }
    const utcFromLocal = new Date(dateTime.getTime() - this.getTimezoneOffset(dateTime));
    return utcFromLocal;
  };

  public static addWeeksToDate(date: Date, weeks: number) {
    date.setDate(date.getDate() + 7 * weeks);
    const firstDay = new Date(date.setDate(date.getDate() - date.getDay()));  
    return firstDay;
  }

  public static getfirstDayOfTheYear(year: number){
    return new Date(`${year}-01-01`);
  }


  public static addMonthsToDate(date: Date, months: number) {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() + months);
    return newDate;
  }
}
